// Pure retail-dashboard metric helpers: stock-filter application and
// aggregation shared by every period shown on the dashboard. Keeping them out
// of the component guarantees that a comparison month is filtered and
// aggregated exactly like the selected month.
import { isRTL } from "../../../../utils";

export const arrayOrEmpty = (value) => (Array.isArray(value) ? value : []);

export const numberOrZero = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const optionalNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export const displayText = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "object") {
    const localizedName = isRTL
      ? value.nameAr || value.name || value.nameEn
      : value.nameEn || value.name || value.nameAr;
    return localizedName || value.label || value.code || value.id || "—";
  }
  return String(value);
};

export const metricCount = (row, metric) => {
  if (metric === "oos") {
    return optionalNumber(row?.trueOOSCount ?? row?.count);
  }
  if (metric === "available") {
    return optionalNumber(row?.shelfAvailableCount);
  }
  if (metric === "gap") {
    return optionalNumber(row?.shelfGapCount);
  }
  return null;
};

export const metricRate = (row, metric) => {
  const rateKey = {
    oos: "trueOOSRate",
    available: "shelfAvailableRate",
    gap: "shelfGapRate",
  }[metric];
  const total = optionalNumber(row?.totalChecks);
  if (total === 0) return null;

  const explicitRate = optionalNumber(row?.[rateKey]);
  if (explicitRate !== null) {
    return Math.max(0, Math.min(100, explicitRate));
  }

  const count = metricCount(row, metric);
  return count !== null && total > 0
    ? Math.max(0, Math.min(100, (count / total) * 100))
    : null;
};

export const stockCategoryKey = (category) => {
  if (category && typeof category === "object") {
    if (category.id !== null && category.id !== undefined && category.id !== "") {
      return `id:${String(category.id)}`;
    }
    const objectLabel =
      category.nameEn || category.name || category.nameAr || category.label;
    if (objectLabel) {
      return `name:${String(objectLabel).trim().toLocaleLowerCase()}`;
    }
  }

  const label = String(category || "uncategorized")
    .trim()
    .toLocaleLowerCase();
  return `name:${label || "uncategorized"}`;
};

export const stockItemKey = (item) => {
  if (item && typeof item === "object") {
    if (
      item.formProductKey !== null &&
      item.formProductKey !== undefined &&
      item.formProductKey !== ""
    ) {
      return `form:${String(item.formProductKey)}`;
    }
    if (item.skuId !== null && item.skuId !== undefined && item.skuId !== "") {
      return `sku:${String(item.skuId)}`;
    }
    const objectLabel = item.sku || item.name || item.label;
    if (objectLabel) {
      return `name:${String(objectLabel).trim().toLocaleLowerCase()}`;
    }
  }

  const label = String(item || "unknown-item").trim().toLocaleLowerCase();
  return `name:${label || "unknown-item"}`;
};

export const rateFromStockCounts = (count, total) =>
  total > 0 ? (count / total) * 100 : null;

export const filterRowsByStockCategories = (data, selectedCategoryKeys) => {
  const rows = arrayOrEmpty(data);
  if (!selectedCategoryKeys.length) return rows;

  const selectedKeys = new Set(selectedCategoryKeys);
  return rows.filter((row) =>
    selectedKeys.has(stockCategoryKey(row?.category)),
  );
};

export const aggregateStockMetrics = (data) => {
  const rows = arrayOrEmpty(data);
  const sum = (key, fallbackKey) =>
    rows.reduce(
      (total, row) =>
        total +
        numberOrZero(
          row?.[key] ?? (fallbackKey ? row?.[fallbackKey] : undefined),
        ),
      0,
    );
  const expectedChecks = sum("expectedChecks", "totalChecks");
  const assessedChecks = sum("assessedChecks", "totalChecks");
  const totalChecks = sum("totalChecks", "assessedChecks");
  const shelfAvailableCount = sum("shelfAvailableCount");
  const shelfGapCount = sum("shelfGapCount");
  const trueOOSCount = sum("trueOOSCount", "count");

  return {
    expectedChecks,
    assessedChecks,
    unassessedChecks: Math.max(0, expectedChecks - assessedChecks),
    assessmentCoverageRate: rateFromStockCounts(
      assessedChecks,
      expectedChecks,
    ),
    totalChecks,
    shelfAvailableCount,
    shelfAvailableRate: rateFromStockCounts(
      shelfAvailableCount,
      totalChecks,
    ),
    shelfGapCount,
    shelfGapRate: rateFromStockCounts(shelfGapCount, totalChecks),
    trueOOSCount,
    trueOOSRate: rateFromStockCounts(trueOOSCount, totalChecks),
    count: trueOOSCount,
  };
};

export const filterRowsByStockSelection = (
  data,
  selectedCategoryKeys,
  selectedItemKeys,
) => {
  const rows = arrayOrEmpty(data);
  if (!selectedCategoryKeys.length && !selectedItemKeys.length) return rows;

  const categoryKeys = new Set(selectedCategoryKeys);
  const itemKeys = new Set(selectedItemKeys);
  return rows.filter(
    (row) =>
      (!categoryKeys.size || categoryKeys.has(stockCategoryKey(row?.category))) &&
      (!itemKeys.size || itemKeys.has(stockItemKey(row))),
  );
};

export const aggregateStockItemsByCategory = (items) => {
  const groups = new Map();
  arrayOrEmpty(items).forEach((item) => {
    const key = stockCategoryKey(item?.category);
    const group = groups.get(key) || {
      category: item?.category,
      items: [],
    };
    group.items.push(item);
    groups.set(key, group);
  });

  return [...groups.values()]
    .map((group) => ({
      category: group.category,
      ...aggregateStockMetrics(group.items),
      monitoredItems: group.items.length,
    }))
    .sort((a, b) =>
      displayText(a?.category).localeCompare(displayText(b?.category), undefined, {
        numeric: true,
        sensitivity: "base",
      }),
    );
};

export const filterStockRowsBySelection = (
  data,
  dimension,
  selectedCategoryKeys,
  selectedItemKeys,
) => {
  const rows = arrayOrEmpty(data);
  const filterActive =
    selectedCategoryKeys.length > 0 || selectedItemKeys.length > 0;
  if (!filterActive) return rows;

  return rows.flatMap((row) => {
    if (
      dimension === "category" &&
      selectedCategoryKeys.length &&
      !selectedCategoryKeys.includes(stockCategoryKey(row?.category))
    ) {
      return [];
    }

    const originalTotalChecks = numberOrZero(row?.totalChecks);
    const sourceItems =
      dimension === "category"
        ? arrayOrEmpty(row?.items)
        : arrayOrEmpty(row?.breakdown?.items);
    const filteredItems = filterRowsByStockSelection(
      sourceItems,
      selectedCategoryKeys,
      selectedItemKeys,
    );

    // Keep task-visited locations that have no submitted stock report. Rows
    // with stock data but no checks matching the selected filters are omitted.
    if (!filteredItems.length && originalTotalChecks > 0) return [];

    const filteredMetrics = aggregateStockMetrics(filteredItems);
    if (dimension === "category") {
      return [
        {
          ...row,
          ...filteredMetrics,
          monitoredItems: filteredItems.length,
          items: filteredItems,
        },
      ];
    }

    const breakdown = {
      ...(row?.breakdown || {}),
      categories: aggregateStockItemsByCategory(filteredItems),
      items: filteredItems,
    };

    // Geographic cross-facets contain all-item totals, so omit them while
    // filtering instead of showing rates that ignore the active selection.
    if (dimension === "region") delete breakdown.cities;
    if (dimension === "channel") delete breakdown.regions;

    return [
      {
        ...row,
        ...filteredMetrics,
        monitoredItems: filteredItems.length,
        breakdown,
      },
    ];
  });
};

export const filterTrendByStockSelection = (
  data,
  selectedCategoryKeys,
  selectedItemKeys,
) => {
  const rows = arrayOrEmpty(data);
  if (!selectedCategoryKeys.length && !selectedItemKeys.length) return rows;

  return rows.map((row) => {
    const sourceRows = selectedItemKeys.length ? row?.byItem : row?.byCategory;
    const filteredRows = selectedItemKeys.length
      ? filterRowsByStockSelection(
          sourceRows,
          selectedCategoryKeys,
          selectedItemKeys,
        )
      : filterRowsByStockCategories(sourceRows, selectedCategoryKeys);
    return {
      ...row,
      ...aggregateStockMetrics(filteredRows),
      ...(selectedItemKeys.length
        ? { byItem: filteredRows }
        : { byCategory: filteredRows }),
    };
  });
};

export const getStockItemOptions = (dashboard, selectedCategoryKeys = []) => {
  const optionMap = new Map();
  const categoryKeys = new Set(selectedCategoryKeys);
  const addOptions = (rows) => {
    arrayOrEmpty(rows).forEach((row) => {
      const categoryKey = stockCategoryKey(row?.category);
      if (categoryKeys.size && !categoryKeys.has(categoryKey)) return;

      const key = stockItemKey(row);
      if (!optionMap.has(key)) {
        optionMap.set(key, {
          value: key,
          label: displayText(row?.sku || row?.name || row?.formProductKey),
          categoryKey,
          categoryLabel: displayText(row?.category),
        });
      }
    });
  };

  arrayOrEmpty(dashboard?.outOfStock?.byCategory).forEach((categoryRow) =>
    addOptions(categoryRow?.items),
  );
  addOptions(dashboard?.inventory?.items);
  addOptions(dashboard?.repeatOOSItems);
  arrayOrEmpty(dashboard?.trend).forEach((row) => addOptions(row?.byItem));

  return [...optionMap.values()].sort(
    (a, b) =>
      a.categoryLabel.localeCompare(b.categoryLabel, undefined, {
        numeric: true,
        sensitivity: "base",
      }) ||
      a.label.localeCompare(b.label, undefined, {
        numeric: true,
        sensitivity: "base",
      }),
  );
};

export const getStockCategoryOptions = (dashboard) => {
  const optionMap = new Map();
  const addOptions = (rows) => {
    arrayOrEmpty(rows).forEach((row) => {
      const key = stockCategoryKey(row?.category);
      if (!optionMap.has(key)) {
        optionMap.set(key, {
          value: key,
          label: displayText(row?.category),
        });
      }
    });
  };

  addOptions(dashboard?.outOfStock?.byCategory);
  addOptions(dashboard?.inventory?.items);
  addOptions(dashboard?.repeatOOSItems);
  arrayOrEmpty(dashboard?.trend).forEach((row) => addOptions(row?.byCategory));

  return [...optionMap.values()].sort((a, b) =>
    a.label.localeCompare(b.label, undefined, {
      numeric: true,
      sensitivity: "base",
    }),
  );
};

/**
 * Apply the stock category/item selection to one period's dashboard payload
 * and derive the values the retail widgets display. Run once per period so
 * both months of a comparison are always filtered identically.
 */
export const deriveRetailView = (
  dashboard,
  { categoryKeys = [], itemKeys = [] } = {},
) => {
  const summary = dashboard?.summary || {};
  const inventory = dashboard?.inventory || {};
  const stockFilterActive = categoryKeys.length > 0 || itemKeys.length > 0;
  const snapshotItemRows = arrayOrEmpty(dashboard?.outOfStock?.byCategory).flatMap(
    (row) => arrayOrEmpty(row?.items),
  );
  const filteredSnapshotItems = filterRowsByStockSelection(
    snapshotItemRows,
    categoryKeys,
    itemKeys,
  );
  const filteredStockSummary = stockFilterActive
    ? aggregateStockMetrics(filteredSnapshotItems)
    : summary;
  const filteredTrend = filterTrendByStockSelection(
    arrayOrEmpty(dashboard?.trend),
    categoryKeys,
    itemKeys,
  );
  const hasCompleteInventoryItems = Array.isArray(inventory?.items);
  const filteredInventoryItems = hasCompleteInventoryItems
    ? filterRowsByStockSelection(inventory.items, categoryKeys, itemKeys)
    : [];
  const filteredRepeatOOSItems = filterRowsByStockSelection(
    arrayOrEmpty(dashboard?.repeatOOSItems),
    categoryKeys,
    itemKeys,
  );
  const stockChecks = numberOrZero(filteredStockSummary?.totalChecks);
  const hasStockChecks = stockChecks > 0;
  const stockRate = (key) =>
    hasStockChecks ? numberOrZero(filteredStockSummary?.[key]) : null;

  return {
    stockFilterActive,
    filteredSnapshotItems,
    filteredStockSummary,
    filteredTrend,
    hasCompleteInventoryItems,
    filteredInventoryItems,
    filteredRepeatOOSItems,
    stockChecks,
    hasStockChecks,
    shelfAvailableRate: stockRate("shelfAvailableRate"),
    trueOOSRate: stockRate("trueOOSRate"),
    shelfGapRate: stockRate("shelfGapRate"),
  };
};

/**
 * One stock-health row per item (snapshot: each client's latest submitted
 * stock report), each carrying a drill-down of the same item by region, city
 * and channel taken from those dimensions' per-item breakdowns.
 */
export const buildItemStockRows = (outOfStock) => {
  const drilldowns = new Map();
  const addDrilldown = (type, rows, locate) => {
    arrayOrEmpty(rows).forEach((row) => {
      arrayOrEmpty(row?.breakdown?.items).forEach((item) => {
        const key = stockItemKey(item);
        const entry = drilldowns.get(key) || {
          regions: [],
          cities: [],
          channels: [],
        };
        entry[type].push({ ...item, ...locate(row) });
        drilldowns.set(key, entry);
      });
    });
  };
  addDrilldown("regions", outOfStock?.byRegion, (row) => ({
    region: row?.region,
  }));
  addDrilldown("cities", outOfStock?.byCity, (row) => ({
    region: row?.region,
    city: row?.city,
  }));
  addDrilldown("channels", outOfStock?.byChannel, (row) => ({
    channel: row?.channel,
    channelLabel: row?.channelLabel,
  }));

  return arrayOrEmpty(outOfStock?.byCategory).flatMap((categoryRow) =>
    arrayOrEmpty(categoryRow?.items).map((item) => ({
      ...item,
      category: item?.category ?? categoryRow?.category,
      breakdown: drilldowns.get(stockItemKey(item)) || {},
    })),
  );
};
